import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { Subject, Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators'
import { ContractSearchDto, ParcelSearchItem, SubjectContactResponseDto } from '../../api/backend'
import { AuthContextStorageService } from '../../core/auth/auth-context-storage.service'
import { CommandPaletteActions } from '../../core/layout/state/command-palette.action-types'
import { selectCommandPaletteIsOpen } from '../../core/layout/state/command-palette.selectors'
import { GoogleAnalyticsService } from '../../core/monitoring/google-analytics.service'
import {
    CATEGORY_LABELS,
    CATEGORY_LIST_TARGETS,
    CATEGORY_ORDER,
    Command,
    CommandCategory
} from './command.model'
import { CaStateService } from '../../core/services/ca-state.service'
import { LIST_SEARCH_FILTER, LIST_SEARCH_NUMBER_FILTER } from '../../constants/list-search-filter.constant'
import { CommandRegistryService } from './services/command-registry.service'
import { EntitySearchResult, EntitySearchService } from './services/entity-search.service'
import { MostVisitedCommandsService } from './services/most-visited-commands.service'

interface CommandGroup {
    category: CommandCategory;
    label: string;
    items: Array<Command>;
    /**
     * Index první položky skupiny ve `flatResults`. Předpočítaný v recomputeResults, aby šablona
     * nemusela pro každý řádek dopočítávat offset procházením všech skupin (bylo O(n²) na každý
     * change detection cyklus).
     */
    flatOffset: number;
    /**
     * Celkový počet odpovídajících záznamů z backendu (0 u neentitních sekcí). Zobrazuje se u názvu
     * sekce, aby uživatel viděl rozsah, i když nic nerozbaluje.
     */
    total: number;
}

const ENTITY_QUERY_MIN_LENGTH = 2
const ENTITY_DEBOUNCE_MS = 300
const GA_CATEGORY = 'command_palette'
const EMPTY_ENTITY_RESULT: EntitySearchResult = {
    subjects: [], contracts: [], parcels: [],
    subjectsTotal: 0, contractsTotal: 0, parcelsTotal: 0,
    contractsMatchedByNumber: false
}

/** České skloňování — „další výsledek / další 3 výsledky / dalších 5 výsledků". */
function remainingLabel(count: number): string {
    if (count === 1) {
        return 'Zobrazit další výsledek'
    }
    if (count < 5) {
        return `Zobrazit další ${count} výsledky`
    }
    return `Zobrazit dalších ${count} výsledků`
}

@Component({
    selector: 'ca-command-palette',
    templateUrl: './command-palette.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CommandPaletteComponent implements OnInit, OnDestroy {

    @ViewChild('searchInput') public searchInput: ElementRef<HTMLInputElement>
    @ViewChildren('itemElement') public itemElements: QueryList<ElementRef<HTMLElement>>

    public isOpen = false
    public query = ''
    public selectedIndex = 0
    public groupedResults: Array<CommandGroup> = []
    public flatResults: Array<Command> = []
    public entityLoading = false

    private allCommands: Array<Command> = []
    private entityResults: EntitySearchResult = EMPTY_ENTITY_RESULT
    /** Sekce rozbalené uživatelem. Resetuje se při každé změně dotazu i při otevření palety. */
    private expandedCategories = new Set<CommandCategory>()
    private expandSubscription: Subscription | null = null
    private readonly query$ = new Subject<string>()
    private readonly subscriptions = new Subscription()

    constructor(
        private readonly registry: CommandRegistryService,
        private readonly store: Store,
        private readonly mostVisited: MostVisitedCommandsService,
        private readonly entitySearch: EntitySearchService,
        private readonly authContext: AuthContextStorageService,
        private readonly googleAnalytics: GoogleAnalyticsService,
        private readonly router: Router,
        private readonly caState: CaStateService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    @HostListener('document:keydown', ['$event'])
    public onDocumentKeydown(event: KeyboardEvent): void {
        // Komponenta žije v app-root (mountovaná v app.component), takže je v DOM
        // i na nepřihlášených stránkách (login). Global zkratky proto smí fungovat
        // jen pro přihlášené — trigger v headeru je beztak jen pro authenticated.
        if (!this.authContext.isAuthenticated()) {
            return
        }

        const triggerOpen = !this.isOpen && (
            (event.key === '/' && !this.isEditableTarget(event.target)) ||
            ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k')
        )

        if (triggerOpen) {
            event.preventDefault()
            const trigger = event.key === '/' ? 'slash' : 'ctrl_k'
            this.googleAnalytics.logEvent(GA_CATEGORY, 'open', trigger)
            this.store.dispatch(CommandPaletteActions.openCommandPalette())
            return
        }

        if (!this.isOpen) {
            return
        }

        switch (event.key) {
            case 'Escape':
                event.preventDefault()
                this.store.dispatch(CommandPaletteActions.closeCommandPalette())
                break
            case 'ArrowDown':
                event.preventDefault()
                this.moveSelection(1)
                break
            case 'ArrowUp':
                event.preventDefault()
                this.moveSelection(-1)
                break
            case 'Enter':
                event.preventDefault()
                this.executeSelected()
                break
        }
    }

    public ngOnInit(): void {
        this.subscriptions.add(
            this.registry.commands.subscribe(commands => {
                this.allCommands = commands
                this.recomputeResults()
                this.cdr.markForCheck()
            })
        )
        this.subscriptions.add(
            this.store.select(selectCommandPaletteIsOpen).subscribe(isOpen => {
                this.isOpen = isOpen
                if (isOpen) {
                    this.query = ''
                    this.selectedIndex = 0
                    this.entityResults = EMPTY_ENTITY_RESULT
                    this.expandedCategories.clear()
                    this.expandSubscription?.unsubscribe()
                    this.entityLoading = false
                    // Reset distinctUntilChanged baseline ve stream query$ — Subject
                    // přežívá zavření palety, takže bez tohoto by rychlé přepsání
                    // stejného dotazu jako minule (debounce zkolaboval klávesy do
                    // jediné emise) distinct zahodil → nenašeptalo by.
                    this.query$.next('')
                    this.recomputeResults()
                    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0)
                }
                this.cdr.markForCheck()
            })
        )
        this.subscriptions.add(
            this.query$.pipe(
                distinctUntilChanged(),
                debounceTime(ENTITY_DEBOUNCE_MS),
                tap(value => {
                    const trimmed = value.trim()
                    if (trimmed.length >= ENTITY_QUERY_MIN_LENGTH) {
                        this.entityLoading = true
                        this.cdr.markForCheck()
                    }
                }),
                switchMap(value => this.entitySearch.search(value))
            ).subscribe(result => {
                this.entityResults = result
                this.entityLoading = false
                this.recomputeResults()
                this.cdr.markForCheck()
                this.logSearchEvent(result)
            })
        )
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe()
        this.expandSubscription?.unsubscribe()
    }

    public onQueryChange(value: string): void {
        this.query = value
        this.selectedIndex = 0
        // Rozbalení platí vždy jen pro konkrétní dotaz — jinak by nový dotaz zdědil rozbalenou sekci,
        // ale data by měl jen do COLLAPSED_LIMIT a sekce by nesmyslně nabízela odkaz do seznamu.
        this.expandedCategories.clear()
        // Rozbalovací request nejde přes switchMap streamu query$, takže se musí zrušit ručně —
        // jinak by jeho pozdější odpověď přepsala výsledky už novějšího dotazu.
        this.expandSubscription?.unsubscribe()
        this.recomputeResults()
        this.query$.next(value)
    }

    public onClickItem(command: Command): void {
        this.executeCommand(command)
    }

    public onMouseEnterItem(index: number): void {
        if (this.selectedIndex !== index) {
            this.selectedIndex = index
            this.cdr.markForCheck()
        }
    }

    public onClickBackdrop(): void {
        this.store.dispatch(CommandPaletteActions.closeCommandPalette())
    }

    public trackById(_: number, command: Command): string {
        return command.id
    }

    public trackByCategory(_: number, group: CommandGroup): string {
        return group.category
    }

    private recomputeResults(): void {
        const isAdmin = this.authContext.isUserAdmin()
        const visible = isAdmin
            ? this.allCommands
            : this.allCommands.filter(c => c.category !== CommandCategory.Admin)

        const query = this.query.trim().toLowerCase()
        const matches = !query
            ? visible
            : visible.filter(c => this.matches(c, query))

        const grouped: Partial<Record<CommandCategory, Array<Command>>> = {}

        // Nejčastější group jen když uživatel nepíše — slouží jako shortcut k nej-
        // používanějším akcím (žebříček řadí BE podle četnosti). Záměrně NEDEDUPLIKUJEME
        // proti ostatním sekcím: položky zůstávají i na svých původních místech (Vytvoř,
        // Admin), Nejčastější je sekundární zkratka. Přesouvání by uživatele matlo
        // (refinement feedback 2026-05-26).
        if (!query) {
            const mostVisitedIds = this.mostVisited.getMostVisitedIds(5)
            const mostVisitedCommands = mostVisitedIds
                .map(id => visible.find(c => c.id === id))
                .filter((c): c is Command => c !== undefined)
            if (mostVisitedCommands.length > 0) {
                grouped[CommandCategory.MostVisited] = mostVisitedCommands
            }
        }

        for (const command of matches) {
            const list = grouped[command.category] ?? []
            list.push(command)
            grouped[command.category] = list
        }

        // Entity search výsledky (Osoby / Smlouvy / Parcely) — jen s query ≥ 2 znaky.
        // Synthetizujeme je jako Command s navigačním action a entity payloadem — keyboard
        // nav + most-visited tracking + GA fungují jednotně, šablona je vykreslí bohatě
        // (přes tytéž pipes jako landing-page-search).
        // Backend při rozbalení vrátí EXPANDED_LIMIT položek pro VŠECHNY tři kategorie, ne jen pro tu
        // rozbalenou — proto se tady krátí podle stavu každé sekce zvlášť. Bez toho by rozbalení osob
        // potichu rozbalilo i smlouvy a parcely.
        if (query.length >= ENTITY_QUERY_MIN_LENGTH) {
            const subjects = this.visibleEntities(CommandCategory.Subjects, this.entityResults.subjects)
            if (subjects.length > 0) {
                grouped[CommandCategory.Subjects] = subjects.map(subject => this.buildSubjectCommand(subject))
            }
            const contracts = this.visibleEntities(CommandCategory.Contracts, this.entityResults.contracts)
            if (contracts.length > 0) {
                grouped[CommandCategory.Contracts] = contracts.map(contract => this.buildContractCommand(contract))
            }
            const parcels = this.visibleEntities(CommandCategory.Parcels, this.entityResults.parcels)
            if (parcels.length > 0) {
                grouped[CommandCategory.Parcels] = parcels.map(parcel => this.buildParcelCommand(parcel))
            }
        }

        let flatOffset = 0
        this.groupedResults = CATEGORY_ORDER
            .filter(cat => (grouped[cat]?.length ?? 0) > 0)
            .map(cat => {
                const results = grouped[cat] as Array<Command>
                const total = this.entityTotal(cat)
                const actionRow = this.buildActionRow(cat, results.length, total)
                const items = actionRow ? [...results, actionRow] : results
                const group = {
                    category: cat,
                    label: CATEGORY_LABELS[cat],
                    items,
                    flatOffset,
                    total
                }
                flatOffset += items.length
                return group
            })

        this.flatResults = this.groupedResults.flatMap(g => g.items)

        if (this.selectedIndex >= this.flatResults.length) {
            this.selectedIndex = Math.max(0, this.flatResults.length - 1)
        }
    }

    private visibleEntities<T>(category: CommandCategory, all: Array<T>): Array<T> {
        const limit = this.expandedCategories.has(category)
            ? EntitySearchService.EXPANDED_LIMIT
            : EntitySearchService.COLLAPSED_LIMIT
        return all.slice(0, limit)
    }

    private entityTotal(category: CommandCategory): number {
        switch (category) {
            case CommandCategory.Subjects:
                return this.entityResults.subjectsTotal
            case CommandCategory.Contracts:
                return this.entityResults.contractsTotal
            case CommandCategory.Parcels:
                return this.entityResults.parcelsTotal
            default:
                return 0
        }
    }

    /**
     * Poslední řádek entitní sekce: dokud jde rozbalit, nabídne rozbalení, potom proklik do seznamu.
     * Vrací null, když je zobrazeno vše (nebo když backend počty neposílá — pak je total 0).
     */
    private buildActionRow(category: CommandCategory, shownCount: number, total: number): Command | null {
        if (total <= shownCount) {
            return null
        }

        if (!this.expandedCategories.has(category)) {
            const remaining = Math.min(total, EntitySearchService.EXPANDED_LIMIT) - shownCount
            return {
                id: `expand:${category}`,
                title: remainingLabel(remaining),
                category,
                rowKind: 'expander',
                icon: 'fa-angle-down',
                action: () => this.expandCategory(category)
            }
        }

        return {
            id: `showAll:${category}`,
            // U parcel se seznam nefiltruje — otevře se filtr Parcely s předvyplněným hledáním
            // a uživatel z nabídky vybírá. Text to musí říct, jinak by odkaz sliboval něco jiného.
            title: category === CommandCategory.Parcels
                ? `Vybrat ze ${total} parcel v seznamu`
                : `Zobrazit všech ${total} v ${CATEGORY_LIST_TARGETS[category]}`,
            category,
            rowKind: 'showAll',
            icon: 'fa-arrow-right',
            action: () => this.openListWithQuery(category)
        }
    }

    /**
     * Dotáhne kategorii na EXPANDED_LIMIT. Vědomě NEJDE přes query$ — ten má debounce
     * a distinctUntilChanged, takže stejný dotaz by se zahodil a nic by se nedotáhlo.
     */
    private expandCategory(category: CommandCategory): void {
        this.expandedCategories.add(category)
        this.googleAnalytics.logEvent(GA_CATEGORY, 'expand_section', category)

        this.expandSubscription?.unsubscribe()
        this.expandSubscription = this.entitySearch.search(this.query, EntitySearchService.EXPANDED_LIMIT)
            .subscribe(result => {
                this.entityResults = result
                this.recomputeResults()
                this.cdr.markForCheck()
            })
    }

    /**
     * Proklik do seznamu entity s předvyplněným hledáním. Používá stejný mechanismus jako dlaždice
     * na landing page (`setFilter` + `resetFilter`), jen s pojmenovaným filtrem `search` a dotazem v `q`.
     */
    private openListWithQuery(category: CommandCategory): void {
        const params = { setFilter: LIST_SEARCH_FILTER, q: this.query.trim(), resetFilter: true }

        switch (category) {
            case CommandCategory.Subjects:
                this.caState.setSubjectListState(params)
                break
            case CommandCategory.Contracts:
                // Backend hledá smlouvy buď podle čísla, nebo podle jména osoby a řekne nám to
                // v contractsMatchedByNumber. Podle toho vybíráme značku filtru, aby seznam
                // předvyplnil totéž kritérium — jinak by dotaz „10" nenašel nic.
                this.caState.setContractListState({
                    ...params,
                    setFilter: this.entityResults.contractsMatchedByNumber
                        ? LIST_SEARCH_NUMBER_FILTER
                        : LIST_SEARCH_FILTER
                })
                break
            case CommandCategory.Parcels:
                this.caState.setParcelsListState(params)
                break
        }
    }

    private buildSubjectCommand(subject: SubjectContactResponseDto): Command {
        const name = subject.nazev ?? [subject.prijmeni, subject.jmeno].filter(Boolean).join(' ')
        return {
            id: `subject:${subject.id}`,
            title: name,
            category: CommandCategory.Subjects,
            subject,
            action: () => this.router.navigate(['/subjects/show', subject.id])
        }
    }

    private buildContractCommand(contract: ContractSearchDto): Command {
        // Route honoruje podpacht stejně jako landing-page-search getContractRouteLink.
        const route = contract.subcontract
            ? ['/subcontract/show', contract.id]
            : ['/contracts/show', contract.id]
        return {
            id: `contract:${contract.id}`,
            title: contract.contractNumberOfficial ?? contract.contractNumber ?? '',
            category: CommandCategory.Contracts,
            contract,
            action: () => this.router.navigate(route)
        }
    }

    private buildParcelCommand(parcel: ParcelSearchItem): Command {
        // Navigace na detail parcely používá parId (skupina verzí), ne technické id.
        return {
            id: `parcel:${parcel.parId}`,
            title: parcel.parcis ?? '',
            category: CommandCategory.Parcels,
            parcel,
            action: () => this.router.navigate(['/parcels/show', parcel.parId])
        }
    }

    private matches(command: Command, query: string): boolean {
        const haystack = [command.title, ...(command.keywords ?? [])].join(' ').toLowerCase()
        return haystack.includes(query)
    }

    private moveSelection(delta: number): void {
        const total = this.flatResults.length
        if (total === 0) {
            return
        }
        this.selectedIndex = (this.selectedIndex + delta + total) % total
        this.scrollSelectionIntoView()
        this.cdr.markForCheck()
    }

    /**
     * Seznam výsledků scrolluje (`.component-commandPalette-results` má overflow-y: auto), takže při
     * pohybu šipkami musí vybraná položka sama odscrollovat do viditelné oblasti — jinak výběr odjede
     * mimo obraz a klávesová navigace přestane být použitelná.
     *
     * `itemElements` sbírá <li> v pořadí šablony, které je totožné s `flatResults`
     * (groupedResults se rendrují v pořadí a flatResults je jejich flatMap), takže lze indexovat přímo
     * `selectedIndex` a nemusí se čekat na propsání `.selected` do DOM.
     */
    private scrollSelectionIntoView(): void {
        this.itemElements?.get(this.selectedIndex)?.nativeElement?.scrollIntoView({ block: 'nearest' })
    }

    private executeSelected(): void {
        const selected = this.flatResults[this.selectedIndex]
        if (selected) {
            this.executeCommand(selected)
        }
    }

    private executeCommand(command: Command): void {
        // Rozbalení sekce není navigace — paleta zůstává otevřená, fokus i dotaz se nemění
        // a do most-visited se nic nezapisuje (jinak by se „Zobrazit dalších 5" tlačilo mezi
        // nejčastější akce). GA event si loguje expandCategory sám.
        if (command.rowKind === 'expander') {
            command.action()
            return
        }

        if (command.rowKind === 'showAll') {
            this.googleAnalytics.logEvent(GA_CATEGORY, 'show_all', command.category)
        } else {
            this.mostVisited.recordExecution(command.id)
            this.googleAnalytics.logEvent(GA_CATEGORY, 'execute', `${command.category}:${command.id}`)
        }

        this.store.dispatch(CommandPaletteActions.closeCommandPalette())
        // Navigaci spustíme až po zavření palety (další tick), aby se stihl
        // promítnout close stav a router-outlet swap nekolidoval s overlay teardownem.
        setTimeout(() => command.action(), 0)
    }

    private logSearchEvent(result: EntitySearchResult): void {
        // GDPR: nikdy nelogujeme text dotazu (může obsahovat jméno / rodné číslo /
        // IČO). Posíláme jen počet výsledků a non-PII bucket — konzistentně
        // s landing-page-search, který taky loguje jen kategorie/count, ne dotaz.
        if (this.query.trim().length < ENTITY_QUERY_MIN_LENGTH) {
            return
        }
        const total = result.subjects.length + result.contracts.length + result.parcels.length
        this.googleAnalytics.logEvent(GA_CATEGORY, 'search', total > 0 ? 'results' : 'empty', total)
    }

    private isEditableTarget(target: EventTarget | null): boolean {
        if (!(target instanceof HTMLElement)) {
            return false
        }
        const tag = target.tagName
        return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable
    }
}